'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  article: string;
  weight: number;
}

interface Section {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'governance',
    title: 'Governance & Management',
    description: 'Management body responsibilities and accountability (Article 20)',
    questions: [
      { id: 'gv1', text: 'Does senior management approve and oversee cybersecurity risk management measures?', article: 'Art. 20(1)', weight: 10 },
      { id: 'gv2', text: 'Are management body members required to undergo cybersecurity training?', article: 'Art. 20(2)', weight: 9 },
      { id: 'gv3', text: 'Is there a formal cybersecurity governance structure in place?', article: 'Art. 20', weight: 9 },
      { id: 'gv4', text: 'Can management body be held liable for cybersecurity compliance?', article: 'Art. 20(1)', weight: 8 },
      { id: 'gv5', text: 'Do you regularly offer cybersecurity training to all employees?', article: 'Art. 20(2)', weight: 8 },
      { id: 'gv6', text: 'Is there documented accountability for cybersecurity at board level?', article: 'Art. 20', weight: 8 },
    ],
  },
  {
    id: 'risk-management',
    title: 'Risk Management Measures',
    description: 'Cybersecurity risk management measures (Article 21)',
    questions: [
      { id: 'rm1', text: 'Do you have documented policies for risk analysis and information system security?', article: 'Art. 21(2)(a)', weight: 10 },
      { id: 'rm2', text: 'Is there a formal incident handling process?', article: 'Art. 21(2)(b)', weight: 10 },
      { id: 'rm3', text: 'Do you have business continuity and crisis management procedures?', article: 'Art. 21(2)(c)', weight: 9 },
      { id: 'rm4', text: 'Is supply chain security assessed and managed?', article: 'Art. 21(2)(d)', weight: 9 },
      { id: 'rm5', text: 'Is there security in network and information systems acquisition, development, and maintenance?', article: 'Art. 21(2)(e)', weight: 8 },
      { id: 'rm6', text: 'Do you have policies for vulnerability handling and disclosure?', article: 'Art. 21(2)(e)', weight: 8 },
      { id: 'rm7', text: 'Are cybersecurity risk management measures regularly assessed for effectiveness?', article: 'Art. 21(2)(f)', weight: 8 },
      { id: 'rm8', text: 'Do you have basic cyber hygiene practices documented?', article: 'Art. 21(2)(g)', weight: 7 },
    ],
  },
  {
    id: 'technical-controls',
    title: 'Technical & Operational Controls',
    description: 'Technical security measures and cryptography (Article 21)',
    questions: [
      { id: 'tc1', text: 'Are cryptography and encryption policies in place where appropriate?', article: 'Art. 21(2)(h)', weight: 9 },
      { id: 'tc2', text: 'Is multi-factor or continuous authentication implemented?', article: 'Art. 21(2)(j)', weight: 9 },
      { id: 'tc3', text: 'Are secure voice, video, and text communications available?', article: 'Art. 21(2)(j)', weight: 8 },
      { id: 'tc4', text: 'Is there secure emergency communication capability?', article: 'Art. 21(2)(j)', weight: 8 },
      { id: 'tc5', text: 'Are access control policies and asset management procedures in place?', article: 'Art. 21(2)(i)', weight: 9 },
      { id: 'tc6', text: 'Is human resources security addressed (before, during, after employment)?', article: 'Art. 21(2)(i)', weight: 8 },
      { id: 'tc7', text: 'Are network and systems monitored for security events?', article: 'Art. 21', weight: 9 },
      { id: 'tc8', text: 'Is there regular security testing (vulnerability scans, penetration tests)?', article: 'Art. 21', weight: 8 },
    ],
  },
  {
    id: 'incident-reporting',
    title: 'Incident Reporting',
    description: 'Incident notification requirements (Article 23)',
    questions: [
      { id: 'ir1', text: 'Can you provide early warning of significant incidents within 24 hours?', article: 'Art. 23(4)(a)', weight: 10 },
      { id: 'ir2', text: 'Can you provide incident notification within 72 hours?', article: 'Art. 23(4)(b)', weight: 10 },
      { id: 'ir3', text: 'Can you provide a final incident report within one month?', article: 'Art. 23(4)(d)', weight: 9 },
      { id: 'ir4', text: 'Is there a process to determine if an incident is significant?', article: 'Art. 23(3)', weight: 9 },
      { id: 'ir5', text: 'Are there procedures for informing recipients of services about incidents?', article: 'Art. 23(1)', weight: 8 },
      { id: 'ir6', text: 'Can you report incidents to the relevant CSIRT/competent authority?', article: 'Art. 23(1)', weight: 9 },
      { id: 'ir7', text: 'Is there a process for cross-border incident notification?', article: 'Art. 23(2)', weight: 7 },
    ],
  },
  {
    id: 'supply-chain',
    title: 'Supply Chain Security',
    description: 'Supply chain and supplier relationship security (Article 21)',
    questions: [
      { id: 'sc1', text: 'Do you assess the cybersecurity practices of direct suppliers?', article: 'Art. 21(2)(d)', weight: 10 },
      { id: 'sc2', text: 'Are supplier contracts including cybersecurity requirements?', article: 'Art. 21(2)(d)', weight: 9 },
      { id: 'sc3', text: 'Do you consider the overall quality of products and practices of suppliers?', article: 'Art. 21(3)', weight: 8 },
      { id: 'sc4', text: 'Are vulnerabilities specific to each supplier assessed?', article: 'Art. 21(3)', weight: 8 },
      { id: 'sc5', text: 'Do you monitor suppliers for cybersecurity issues?', article: 'Art. 21(2)(d)', weight: 8 },
      { id: 'sc6', text: 'Is there a process for managing supplier-related incidents?', article: 'Art. 21(2)(d)', weight: 8 },
    ],
  },
  {
    id: 'business-continuity',
    title: 'Business Continuity',
    description: 'Business continuity, backup management, and disaster recovery (Article 21)',
    questions: [
      { id: 'bc1', text: 'Do you have documented business continuity plans?', article: 'Art. 21(2)(c)', weight: 10 },
      { id: 'bc2', text: 'Are backup management procedures in place?', article: 'Art. 21(2)(c)', weight: 9 },
      { id: 'bc3', text: 'Is there a disaster recovery capability?', article: 'Art. 21(2)(c)', weight: 9 },
      { id: 'bc4', text: 'Is there a crisis management framework?', article: 'Art. 21(2)(c)', weight: 9 },
      { id: 'bc5', text: 'Are business continuity plans regularly tested?', article: 'Art. 21(2)(c)', weight: 8 },
      { id: 'bc6', text: 'Are recovery time objectives defined for critical services?', article: 'Art. 21(2)(c)', weight: 8 },
    ],
  },
  {
    id: 'registration',
    title: 'Registration & Supervision',
    description: 'Entity registration and supervisory requirements (Articles 27-28)',
    questions: [
      { id: 'rg1', text: 'Have you identified which Member States your entity falls under?', article: 'Art. 26', weight: 8 },
      { id: 'rg2', text: 'Are you prepared to register with relevant competent authorities?', article: 'Art. 27', weight: 9 },
      { id: 'rg3', text: 'Can you provide required registration information?', article: 'Art. 27(2)', weight: 8 },
      { id: 'rg4', text: 'Are you aware of supervisory powers applicable to your entity?', article: 'Art. 32', weight: 7 },
      { id: 'rg5', text: 'Is there a process for responding to regulatory requests?', article: 'Art. 32', weight: 7 },
    ],
  },
];

const entityTypes = [
  { value: 'energy', label: 'Energy (electricity, oil, gas, hydrogen)', sector: 'essential' },
  { value: 'transport', label: 'Transport (air, rail, water, road)', sector: 'essential' },
  { value: 'banking', label: 'Banking', sector: 'essential' },
  { value: 'financial-infrastructure', label: 'Financial Market Infrastructure', sector: 'essential' },
  { value: 'health', label: 'Health (hospitals, labs, pharma)', sector: 'essential' },
  { value: 'drinking-water', label: 'Drinking Water Supply', sector: 'essential' },
  { value: 'waste-water', label: 'Waste Water', sector: 'essential' },
  { value: 'digital-infrastructure', label: 'Digital Infrastructure (DNS, TLD, cloud, data centers)', sector: 'essential' },
  { value: 'ict-services', label: 'ICT Service Management (B2B)', sector: 'essential' },
  { value: 'public-administration', label: 'Public Administration', sector: 'essential' },
  { value: 'space', label: 'Space', sector: 'essential' },
  { value: 'postal', label: 'Postal and Courier Services', sector: 'important' },
  { value: 'waste-management', label: 'Waste Management', sector: 'important' },
  { value: 'chemicals', label: 'Manufacture/Production of Chemicals', sector: 'important' },
  { value: 'food', label: 'Food Production, Processing, Distribution', sector: 'important' },
  { value: 'manufacturing', label: 'Manufacturing (medical devices, computers, machinery, vehicles)', sector: 'important' },
  { value: 'digital-providers', label: 'Digital Providers (marketplaces, search engines, social networks)', sector: 'important' },
  { value: 'research', label: 'Research Organizations', sector: 'important' },
];

export default function NIS2Readiness() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [entityType, setEntityType] = useState<string>('');
  const [entitySize, setEntitySize] = useState<string>('');
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'nis2-readiness',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const getEntityClassification = () => {
    const entity = entityTypes.find((e) => e.value === entityType);
    if (!entity) return null;

    if (entity.sector === 'essential') {
      return { type: 'Essential Entity', color: 'text-red-400', bg: 'bg-red-500/20' };
    }
    if (entitySize === 'large' || entitySize === 'medium') {
      return { type: 'Important Entity', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    }
    return { type: 'May be out of scope', color: 'text-gray-400', bg: 'bg-gray-500/20' };
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { article: string; question: string; status: string }[] = [];

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({ article: q.article, question: q.text, status: 'Partial' });
        } else if (answer === 'no') {
          gaps.push({ article: q.article, question: q.text, status: 'Not Implemented' });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps: gaps.sort((a, b) => a.article.localeCompare(b.article)),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getComplianceLevel = (score: number) => {
    if (score >= 90) return { level: 'Well Prepared', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 70) return { level: 'Substantially Ready', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 50) return { level: 'Partially Ready', color: 'text-accent-400', bg: 'bg-accent-500/20' };
    return { level: 'Significant Gaps', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const compliance = getComplianceLevel(score);
    const classification = getEntityClassification();

    let md = `# NIS2 Readiness Assessment Report\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n\n`;
    md += `**Entity Type:** ${entityTypes.find((e) => e.value === entityType)?.label || 'Not specified'}\n`;
    md += `**Entity Size:** ${entitySize || 'Not specified'}\n`;
    md += `**Classification:** ${classification?.type || 'Not determined'}\n\n`;
    md += `## Overall Readiness Score\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Status:** ${compliance.level}\n\n`;

    md += `## Section Scores\n\n`;
    md += `| Section | Score |\n`;
    md += `|---------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Compliance Gaps\n\n`;
      md += `| Article | Issue | Status |\n`;
      md += `|---------|-------|--------|\n`;
      gaps.forEach((gap) => {
        md += `| ${gap.article} | ${gap.question} | ${gap.status} |\n`;
      });
    }

    md += `\n## NIS2 Key Deadlines\n\n`;
    md += `| Date | Milestone |\n`;
    md += `|------|----------|\n`;
    md += `| January 16, 2023 | NIS2 Directive entered into force |\n`;
    md += `| October 17, 2024 | Member State transposition deadline |\n`;
    md += `| October 18, 2024 | NIS2 requirements become applicable |\n`;
    md += `| April 17, 2025 | List of essential and important entities due |\n`;

    md += `\n## Penalties for Non-Compliance\n\n`;
    md += `**Essential Entities:** Up to €10 million or 2% of global annual turnover\n`;
    md += `**Important Entities:** Up to €7 million or 1.4% of global annual turnover\n`;
    md += `\nManagement body members can also face personal liability.\n`;

    md += `\n## Recommended Actions\n\n`;
    if (score < 90) {
      md += `1. Establish or enhance governance accountability for cybersecurity\n`;
      md += `2. Implement comprehensive risk management measures\n`;
      md += `3. Develop incident detection and reporting capabilities\n`;
      md += `4. Assess and secure supply chain relationships\n`;
      md += `5. Ensure business continuity and crisis management capabilities\n`;
      md += `6. Register with competent authority in applicable Member States\n`;
    } else {
      md += `1. Maintain current security posture\n`;
      md += `2. Monitor Member State implementation for specific requirements\n`;
      md += `3. Continue regular security assessments\n`;
      md += `4. Ensure ongoing management body training\n`;
    }

    md += `\n---\n*Assessment conducted using The Art of CTO NIS2 Readiness Assessment*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nis2-readiness-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const { score, gaps } = calculateScore();
    const compliance = getComplianceLevel(score);
    const classification = getEntityClassification();

    const gapItems = gaps.map((gap) => ({
      id: gap.article.toLowerCase().replace(/[^a-z0-9]/g, '-'),
      title: gap.question,
      severity: gap.status === 'Not Implemented' ? 'high' as const : 'medium' as const,
      description: `${gap.status} - Article ${gap.article}`,
    }));

    const sectionScores: Record<string, number> = {};
    sections.forEach((section) => {
      sectionScores[section.id] = getSectionScore(section.id);
    });

    const recommendations = sections
      .filter((s) => getSectionScore(s.id) < 80)
      .sort((a, b) => getSectionScore(a.id) - getSectionScore(b.id))
      .map((section) => ({
        id: section.id,
        title: `Improve ${section.title}`,
        priority: getSectionScore(section.id) < 50 ? 'high' as const :
                  getSectionScore(section.id) < 70 ? 'high' as const : 'medium' as const,
        description: `Current score: ${getSectionScore(section.id)}%. Address NIS2 requirements for ${section.title.toLowerCase()}.`,
      }));

    const assessment = await saveResult({
      name: `NIS2 Readiness - ${new Date().toLocaleDateString()}`,
      score_overall: score,
      scores: sectionScores,
      answers,
      gaps: gapItems,
      recommendations,
      metadata: {
        complianceLevel: compliance.level,
        entityType: entityTypes.find((e) => e.value === entityType)?.label || 'Not specified',
        entitySize: entitySize || 'Not specified',
        classification: classification?.type || 'Not determined',
        totalQuestions: sections.reduce((sum, s) => sum + s.questions.length, 0),
        answeredQuestions: Object.keys(answers).length,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              EU Critical Infrastructure
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              NIS2 Readiness Assessment
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Assess your organization&apos;s readiness for the Network and Information Security Directive 2 (NIS2).
              Identify gaps in cybersecurity governance, risk management, and incident reporting.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">What is NIS2?</h3>
              <p className="text-foreground-secondary text-sm">
                The NIS2 Directive (EU 2022/2555) is the EU&apos;s updated cybersecurity legislation
                that significantly expands the scope of entities required to implement
                cybersecurity measures and report incidents.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Who Must Comply?</h3>
              <p className="text-foreground-secondary text-sm">
                Medium and large entities in 18 sectors including energy, transport, banking,
                health, digital infrastructure, public administration, and more.
                Applies to ~160,000 entities across the EU.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">Determine Your Scope</h3>

            <div className="mb-4">
              <label className="text-foreground-secondary text-sm block mb-2">
                What sector does your organization operate in?
              </label>
              <select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
              >
                <option value="">Select sector...</option>
                <optgroup label="Essential Entities (Annex I)">
                  {entityTypes.filter((e) => e.sector === 'essential').map((e) => (
                    <option key={e.value} value={e.value}>{e.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Important Entities (Annex II)">
                  {entityTypes.filter((e) => e.sector === 'important').map((e) => (
                    <option key={e.value} value={e.value}>{e.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>

            <div>
              <label className="text-foreground-secondary text-sm block mb-2">
                What is your organization size?
              </label>
              <select
                value={entitySize}
                onChange={(e) => setEntitySize(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
              >
                <option value="">Select size...</option>
                <option value="large">Large (&gt;250 employees or &gt;€50M turnover)</option>
                <option value="medium">Medium (50-250 employees or €10-50M turnover)</option>
                <option value="small">Small (&lt;50 employees and &lt;€10M turnover)</option>
              </select>
            </div>

            {entityType && entitySize && (
              <div className="mt-4 p-3 rounded-lg bg-cream/5 border border-border-muted">
                {(() => {
                  const classification = getEntityClassification();
                  return classification ? (
                    <p className="text-sm">
                      <span className="text-foreground-secondary">Your preliminary classification: </span>
                      <span className={`font-semibold ${classification.color}`}>
                        {classification.type}
                      </span>
                    </p>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">Key NIS2 Requirements</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-foreground-secondary">
              <div>
                <p className="mb-2"><span className="text-accent">•</span> Risk management measures</p>
                <p className="mb-2"><span className="text-accent">•</span> Incident handling and reporting</p>
                <p className="mb-2"><span className="text-accent">•</span> Business continuity management</p>
                <p className="mb-2"><span className="text-accent">•</span> Supply chain security</p>
              </div>
              <div>
                <p className="mb-2"><span className="text-accent">•</span> Management body accountability</p>
                <p className="mb-2"><span className="text-accent">•</span> Cybersecurity training</p>
                <p className="mb-2"><span className="text-accent">•</span> Cryptography and access control</p>
                <p className="mb-2"><span className="text-accent">•</span> Vulnerability disclosure</p>
              </div>
            </div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6 mb-8">
            <h3 className="text-accent font-semibold mb-2">Key Deadline</h3>
            <p className="text-foreground">
              <span className="font-bold">October 18, 2024:</span>{' '}
              <span className="text-foreground-secondary">
                NIS2 requirements become applicable across EU Member States.
              </span>
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start Readiness Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 15-20 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      {question.article}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Section
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, gaps } = calculateScore();
  const compliance = getComplianceLevel(score);
  const classification = getEntityClassification();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <button
            onClick={exportToMarkdown}
            className="bg-cream/10 hover:bg-cream/20 text-foreground px-4 py-2 rounded-lg transition-colors"
          >
            Export Report
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            NIS2 Readiness Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${compliance.bg}`}>
            <span className={`text-4xl font-bold ${compliance.color}`}>{score}%</span>
            <p className={`${compliance.color} font-medium mt-1`}>{compliance.level}</p>
          </div>
          {classification && (
            <p className={`text-sm mt-4 ${classification.color}`}>
              Classification: {classification.type}
            </p>
          )}
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Section Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionCompliance = getComplianceLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionCompliance.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 90
                          ? 'bg-green-500'
                          : sectionScore >= 70
                          ? 'bg-yellow-500'
                          : sectionScore >= 50
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Compliance Gaps ({gaps.length})
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-accent text-xs font-medium">
                        {gap.article}
                      </span>
                      <p className="text-foreground text-sm mt-1">{gap.question}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                        gap.status === 'Partial'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {gap.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Penalties</h2>
          <div className="text-foreground-secondary text-sm space-y-3">
            <div>
              <h4 className="text-red-400 font-medium mb-1">Essential Entities</h4>
              <p>Up to €10,000,000 or 2% of total worldwide annual turnover (whichever is higher)</p>
            </div>
            <div>
              <h4 className="text-yellow-400 font-medium mb-1">Important Entities</h4>
              <p>Up to €7,000,000 or 1.4% of total worldwide annual turnover (whichever is higher)</p>
            </div>
            <p className="text-foreground-tertiary mt-4">
              Management body members can face personal liability and temporary bans from management roles.
            </p>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Supervision Regime</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p>
              <span className="text-accent font-medium">Essential Entities:</span>{' '}
              Ex-ante supervision including regular security audits, on-site inspections,
              and evidence-based security measures verification.
            </p>
            <p>
              <span className="text-accent font-medium">Important Entities:</span>{' '}
              Ex-post supervision triggered by evidence of non-compliance, typically
              following incident reports or complaints.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mt-8">
          {commandCenterEnabled && !saved && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save to Command Center'}
            </button>
          )}
          {saved && (
            <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Saved to Command Center
            </div>
          )}
          {saveError && (
            <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
              {saveError}
            </div>
          )}
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setSaved(false);
            }}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:border-accent transition-colors"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
